// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseAvatar, BasePagination, Loading } from '~/components/common';
// Others
import { getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IQueryParamBase } from '~/utils/interface/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IForemanPerformance } from '~/utils/interface/statistic';
import { getStatisticForemanPerformance } from '~/thunks/statistic/statisticThunk';
import { DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE_DASHBOARD } from '~/utils/constants/common';
// Styles, images, icons
import styles from './ForemanPerformanceTracking.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const ForemanPerformanceTracking = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [listData, setListData] = useState<IForemanPerformance[]>([]);

  const [paramObject, setParamObject] = useState<IQueryParamBase>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE_DASHBOARD,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListForemanPerformance(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListForemanPerformance = (params: IQueryParamBase) => {
    setIsLoading(true);

    dispatch(getStatisticForemanPerformance(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setListData(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
  };
  //#endregion Handle Function

  return (
    <Box id='foremanPerformanceTrackingComponent' className={cx('container')}>
      <Stack direction={'column'} spacing={'13px'}>
        <Typography className={cx('title')}>{t('dashboard_title_performance')}</Typography>

        <Typography className={cx('description')}>{t('dashboard_title_performance_description')}</Typography>
      </Stack>

      <Stack className={cx('content')}>
        {listData && listData.length > 0 ? (
          listData.map((item) => (
            <Stack key={item?.id} className={cx('viewItem')}>
              <Stack direction={'row'} gap={4} alignItems={'center'} flex={1}>
                <BaseAvatar
                  dataAccount={{
                    firstName: item?.firstName,
                    lastName: item?.lastName,
                    avatarImage: item?.avatarImage,
                  }}
                  size={40}
                  fontSize={14}
                />

                <Stack direction={'column'}>
                  <Typography className={cx('textName')}>
                    {getFullName({
                      firstName: item?.firstName,
                      lastName: item?.lastName,
                    })}
                  </Typography>
                  <Typography className={cx('textForeman')}>{t('foreman_performance_title_foreman')}</Typography>
                </Stack>
              </Stack>

              <Stack direction={'row'} gap={3}>
                <Stack className={cx('viewTask')}>
                  <Typography className={cx('titleTask')}>{t('foreman_performance_title_tasks_completed')}</Typography>

                  <Typography className={cx('value')}>{`${Number(item?.taskCompleted)}/${Number(
                    item?.taskTotal
                  )}`}</Typography>
                </Stack>
              </Stack>

              <Stack>
                <Typography className={cx('value')}>{`${Number(item.completionRate)}%`}</Typography>

                <Typography className={cx('titleEfficiency')}>{t('foreman_performance_title_efficiency')}</Typography>
              </Stack>
            </Stack>
          ))
        ) : (
          <Typography align='center' className={cx('noData')}>
            {t('common_no_data_available', { ns: 'common/base' })}
          </Typography>
        )}
      </Stack>

      <BasePagination count={pagination?.totalPages} page={pagination?.page} onChange={handlePageChange} />

      {isLoading && <Loading />}
    </Box>
  );
};

export default ForemanPerformanceTracking;
